import React, { useMemo } from "react";
import { SvgV2 } from "@whatslly/ui-components/v2/BaseElements/SvgV2";
import styles from "./AccountSelect.module.scss";
import { useDependencyInjector } from "@whatslly/commons/di";
import { observer } from "mobx-react-lite";
import { AsyncSelect } from "@whatslly/ui-components/v2/BaseElements/Select";
import { useTranslation } from "react-i18next";
import { FiltersStore } from "../../../../services/filtersStore";
import { Svg } from "@whatslly/ui-components/v2/BaseElements/Svg";
import { HeaderButton } from "@whatslly/ui-components/HeaderButton";
import { AccountStore } from "../../../../services/AccountStore";
import { Link } from "react-router-dom";
import type { IUserStore } from "@whatslly/ui-components/services/UserStore";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
export const AccountSelectionSelectComponent: React.FC<{
  showClear?: boolean;
}> = observer(({ showClear = true }) => {
  const accountStore = useDependencyInjector<AccountStore>(AccountStore);
  const filterStore = useDependencyInjector<FiltersStore>(FiltersStore);
  const { t } = useTranslation();
  const searchContainerRef = React.useRef<HTMLDivElement>(null);
  const [value, onChange] = useMemo(() => {
    return [
      accountStore.account.case({
        fulfilled: (account) =>
          account
            ? {
                value: account.id,
                label: account.name,
              }
            : null,
        pending: () => undefined,
        rejected: () => undefined,
      }),
      (selectedOption: { label?: string; value: string }) => (filterStore.accountId = selectedOption?.value),
    ];
  }, [filterStore, accountStore.account.state, accountStore.account]);
  return (
    <>
      <div className={styles.superAdminContainer}>
        <div
          className={styles.searchContainer}
          ref={searchContainerRef}
          onClick={() => {
            searchContainerRef.current?.querySelector("input")?.focus();
          }}
        >
          <Svg className={styles.searchIcon} iconName="search-actions" />
          <AsyncSelect
            showClear={showClear}
            hideDropdownIndicator
            menuWidth="500px"
            value={value}
            data-testid="account-select"
            borderColor="none"
            selectSize={"small"}
            controlClassName={styles.selectControl}
            loadOptionsFromServerFunction={accountStore.searchAccounts}
            placeholder={t("search_accounts.search_accounts")}
            onChange={onChange}
          />
        </div>
      </div>
    </>
  );
});

export const AccountSelect: React.FC = observer(() => {
  const accountStore = useDependencyInjector<AccountStore>(AccountStore);
  const userStore = useDependencyInjector<IUserStore>(InjectUserStore);
  const { t } = useTranslation();
  return (
    <div className={styles.superAdminAndLogoContainer}>
      <Link to="/home">
        <SvgV2 className={styles.logo} iconName={`tuvis`} width={40} height={40} />
      </Link>
      <AccountSelectionSelectComponent />
      {userStore.isAnyAdmin && (
        <HeaderButton
          tooltipText={t("search_accounts.create_new_account")}
          testId="create-new-account"
          onClick={() => accountStore.createNewAccount()}
        >
          <SvgV2 iconName={"plus"} />
        </HeaderButton>
      )}
      {accountStore.account.state === "fulfilled" && accountStore.account.value && (
        <HeaderButton
          tooltipText={t("search_accounts.change_account_name")}
          testId="rename-account"
          onClick={() => accountStore.editAccountName()}
          children={<SvgV2 iconName={`pencile`} />}
        />
      )}
    </div>
  );
});
